import { useCallback, useEffect, useRef, useState } from 'react';

import { Box, SxProps, Theme, useTheme } from '@mui/material';

import { RequirementSectionKey } from './TradeoffResultsPanel';

/**
 * Curated mapping of aspiration category → right-side sections it primarily affects.
 * Used to highlight relevant connection lines on hover. Not derived from scoring code:
 * tier-driven sections (Career Track, Education) technically respond to most categories
 * via point_value sums, but this map reflects the *intent* designers want to communicate.
 */
const CATEGORY_TO_PRIMARY_SECTIONS: Record<string, RequirementSectionKey[]> = {
  lifestyle_and_home: ['careerTrack', 'education', 'ownership'],
  locations_and_options: ['careerTrack', 'education'],
  family_and_caregiving: ['careerTrack', 'education', 'careerPaths'],
  work_settings: ['careerTrack', 'careerPaths'],
  career_attributes: ['careerTrack', 'education'],
  building_wealth: ['careerTrack', 'education', 'ownership'],
  sources_of_meaning: ['careerPaths'],
  activities: ['careerTrack', 'education', 'careerPaths'],
};

interface Geometry {
  spineX: number;
  leftStubs: { key: string; x1: number; y: number }[];
  rightStubs: { key: RequirementSectionKey; x2: number; y: number }[];
  spineTop: number;
  spineBottom: number;
}

type TradeoffConnectionLinesProps = Readonly<{
  containerRef: React.RefObject<HTMLDivElement | null>;
  aspirationRefs: React.RefObject<Map<string, HTMLElement>>;
  requirementRefs: React.RefObject<Map<RequirementSectionKey, HTMLElement>>;
  hoveredCategoryKey?: string | null;
}>;

/** Duration to keep the rAF loop running (covers 300ms MUI Collapse + buffer) */
const ANIMATION_DURATION = 350;
const DIM_OPACITY = 0.25;
const FULL_OPACITY = 1;

export function TradeoffConnectionLines({
  containerRef,
  aspirationRefs,
  requirementRefs,
  hoveredCategoryKey = null,
}: TradeoffConnectionLinesProps) {
  const theme = useTheme();
  const strokeColor = theme.backrs.colors['Brand/500'];

  const [geometry, setGeometry] = useState<Geometry | null>(null);

  const spineRef = useRef<SVGLineElement | null>(null);
  const overlaySpineRef = useRef<SVGLineElement | null>(null);
  const leftLineRefs = useRef<Map<number, SVGLineElement>>(new Map());
  const rightLineRefs = useRef<Map<number, SVGLineElement>>(new Map());

  // Read by the rAF loop so the overlay spine can track accordion animations
  // without waiting for React state to flush at the end of the loop.
  const hoverStateRef = useRef<{
    hoveredCategoryKey: string | null;
    primarySections: Set<RequirementSectionKey> | null;
  }>({ hoveredCategoryKey: null, primarySections: null });

  const calculate = useCallback((): Geometry | null => {
    const container = containerRef.current;
    const aspirations = aspirationRefs.current;
    const requirements = requirementRefs.current;

    if (!container || !aspirations || !requirements) return null;
    if (aspirations.size === 0 || requirements.size === 0) return null;

    const containerRect = container.getBoundingClientRect();

    const leftStubs = Array.from(aspirations.entries()).map(([key, el]) => {
      const rect = el.getBoundingClientRect();

      return {
        key,
        x1: rect.right - containerRect.left,
        y: rect.top + rect.height / 2 - containerRect.top,
      };
    });

    const rightStubs = Array.from(requirements.entries()).map(([key, el]) => {
      const rect = el.getBoundingClientRect();

      return {
        key,
        x2: rect.left - containerRect.left,
        y: rect.top + rect.height / 2 - containerRect.top,
      };
    });

    const maxLeftX = Math.max(...leftStubs.map((s) => s.x1));
    const minRightX = Math.min(...rightStubs.map((s) => s.x2));
    const spineX = (maxLeftX + minRightX) / 2;

    const allYs = [...leftStubs.map((s) => s.y), ...rightStubs.map((s) => s.y)];
    const spineTop = Math.min(...allYs);
    const spineBottom = Math.max(...allYs);

    return { spineX, leftStubs, rightStubs, spineTop, spineBottom };
  }, [containerRef, aspirationRefs, requirementRefs]);

  // Apply geometry directly to <line> DOM nodes — bypasses React for smooth per-frame tracking.
  const applyGeometry = useCallback((g: Geometry) => {
    const spineEl = spineRef.current;

    if (spineEl) {
      spineEl.setAttribute('x1', String(g.spineX));
      spineEl.setAttribute('y1', String(g.spineTop));
      spineEl.setAttribute('x2', String(g.spineX));
      spineEl.setAttribute('y2', String(g.spineBottom));
    }

    g.leftStubs.forEach((stub, i) => {
      const el = leftLineRefs.current.get(i);

      if (el) {
        el.setAttribute('x1', String(stub.x1));
        el.setAttribute('y1', String(stub.y));
        el.setAttribute('x2', String(g.spineX));
        el.setAttribute('y2', String(stub.y));
      }
    });

    g.rightStubs.forEach((stub, i) => {
      const el = rightLineRefs.current.get(i);

      if (el) {
        el.setAttribute('x1', String(g.spineX));
        el.setAttribute('y1', String(stub.y));
        el.setAttribute('x2', String(stub.x2));
        el.setAttribute('y2', String(stub.y));
      }
    });

    const overlayEl = overlaySpineRef.current;
    const { hoveredCategoryKey: hk, primarySections: ps } = hoverStateRef.current;

    if (overlayEl && hk && ps) {
      const activeYs = [
        ...g.leftStubs.filter((s) => s.key === hk).map((s) => s.y),
        ...g.rightStubs.filter((s) => ps.has(s.key)).map((s) => s.y),
      ];

      if (activeYs.length > 0) {
        overlayEl.setAttribute('x1', String(g.spineX));
        overlayEl.setAttribute('y1', String(Math.min(...activeYs)));
        overlayEl.setAttribute('x2', String(g.spineX));
        overlayEl.setAttribute('y2', String(Math.max(...activeYs)));
      }
    }
  }, []);

  const observerRef = useRef<ResizeObserver | null>(null);
  const mutationObserverRef = useRef<MutationObserver | null>(null);
  const rafRef = useRef(0);
  const animatingRef = useRef(false);
  const animationEndRef = useRef(0);

  useEffect(() => {
    const runAnimation = () => {
      // If already looping, just extend the deadline so subsequent triggers
      // (e.g. mid-Collapse mutations) keep the loop alive instead of starting fresh.
      if (animatingRef.current) {
        animationEndRef.current = performance.now() + ANIMATION_DURATION;

        return;
      }

      animatingRef.current = true;
      animationEndRef.current = performance.now() + ANIMATION_DURATION;

      const loop = () => {
        const next = calculate();

        if (next) applyGeometry(next);

        if (performance.now() < animationEndRef.current) {
          rafRef.current = requestAnimationFrame(loop);
        } else {
          // Animation done — sync React state once so the line element count stays correct.
          animatingRef.current = false;

          if (next) setGeometry(next);
        }
      };

      rafRef.current = requestAnimationFrame(loop);
    };

    // Defer initial calculation so refs from layout-effect children are populated.
    const initId = requestAnimationFrame(() => setGeometry(calculate()));

    const container = containerRef.current;

    if (container) {
      observerRef.current = new ResizeObserver(runAnimation);
      observerRef.current.observe(container);

      // Catches accordion expand/collapse (child DOM structural & style changes)
      mutationObserverRef.current = new MutationObserver(runAnimation);
      mutationObserverRef.current.observe(container, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['style', 'class'],
      });
    }

    return () => {
      cancelAnimationFrame(initId);
      cancelAnimationFrame(rafRef.current);
      animatingRef.current = false;
      observerRef.current?.disconnect();
      mutationObserverRef.current?.disconnect();
    };
  }, [calculate, applyGeometry, containerRef]);

  const primarySections = hoveredCategoryKey
    ? new Set(CATEGORY_TO_PRIMARY_SECTIONS[hoveredCategoryKey] ?? [])
    : null;

  // Keep the rAF loop's view of hover state in sync without remounting observers.
  hoverStateRef.current = { hoveredCategoryKey: hoveredCategoryKey ?? null, primarySections };

  if (!geometry) return null;

  const { spineX, leftStubs, rightStubs, spineTop, spineBottom } = geometry;

  const leftOpacity = (key: string) =>
    !hoveredCategoryKey || key === hoveredCategoryKey ? FULL_OPACITY : DIM_OPACITY;

  const rightOpacity = (key: RequirementSectionKey) =>
    !primarySections || primarySections.has(key) ? FULL_OPACITY : DIM_OPACITY;

  // When hovering, the overlay spine spans only between the topmost and
  // bottommost active branches — the dim base spine still shows above/below.
  const activeYs =
    hoveredCategoryKey && primarySections
      ? [
          ...leftStubs.filter((s) => s.key === hoveredCategoryKey).map((s) => s.y),
          ...rightStubs.filter((s) => primarySections.has(s.key)).map((s) => s.y),
        ]
      : [];
  const isHoverActive = activeYs.length > 0;
  const activeTop = isHoverActive ? Math.min(...activeYs) : spineTop;
  const activeBottom = isHoverActive ? Math.max(...activeYs) : spineBottom;

  return (
    <Box component="svg" sx={svgSx}>
      <line
        ref={spineRef}
        x1={spineX}
        y1={spineTop}
        x2={spineX}
        y2={spineBottom}
        stroke={strokeColor}
        strokeWidth={1.5}
        strokeOpacity={hoveredCategoryKey ? DIM_OPACITY : FULL_OPACITY}
        style={lineTransitionStyle}
      />
      <line
        ref={overlaySpineRef}
        x1={spineX}
        y1={activeTop}
        x2={spineX}
        y2={activeBottom}
        stroke={strokeColor}
        strokeWidth={1.5}
        strokeOpacity={isHoverActive ? FULL_OPACITY : 0}
        style={lineTransitionStyle}
      />
      {leftStubs.map((stub, i) => (
        <line
          key={`l-${i}`}
          ref={(el) => {
            if (el) leftLineRefs.current.set(i, el);
            else leftLineRefs.current.delete(i);
          }}
          x1={stub.x1}
          y1={stub.y}
          x2={spineX}
          y2={stub.y}
          stroke={strokeColor}
          strokeWidth={1.5}
          strokeOpacity={leftOpacity(stub.key)}
          style={lineTransitionStyle}
        />
      ))}
      {rightStubs.map((stub, i) => (
        <line
          key={`r-${i}`}
          ref={(el) => {
            if (el) rightLineRefs.current.set(i, el);
            else rightLineRefs.current.delete(i);
          }}
          x1={spineX}
          y1={stub.y}
          x2={stub.x2}
          y2={stub.y}
          stroke={strokeColor}
          strokeWidth={1.5}
          strokeOpacity={rightOpacity(stub.key)}
          style={lineTransitionStyle}
        />
      ))}
    </Box>
  );
}

const lineTransitionStyle = { transition: 'stroke-opacity 0.15s ease-in-out' };

const svgSx: SxProps<Theme> = {
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  pointerEvents: 'none',
  zIndex: 1,
  overflow: 'visible',
  display: 'none',
  '@container (min-width: 600px)': {
    display: 'block',
  },
};
